/**
 * Сохранённые места семьи.
 *
 * Смысл в одной строке: вместо «55.751244, 37.618423» в ленте появляется
 * «рядом: Сад №12». Координаты родителю ничего не говорят, название говорит всё.
 *
 * Новые места добавляются из ленты («Сохранить как место») — там уже есть
 * точка, и вводить широту руками не нужно. Здесь только список и удаление.
 */

import { usePlaces, useArchivePlace, type Place } from '@/shared/api/trust'
import { Sheet } from '@/shared/ui/Sheet'
import { Icon } from '@/shared/ui/Icon'
import { Card, Row } from '@/shared/ui/primitives'
import { EmptyState, SkeletonCard } from '@/shared/ui/states'
import { toast } from '@/shared/ui/toast'

export function PlacesSheet({
  open,
  familyId,
  onClose,
}: {
  open: boolean
  familyId: number
  onClose: () => void
}) {
  const places = usePlaces(familyId)
  const archive = useArchivePlace(familyId, {
    onSuccess: () => toast.show('Место удалено'),
    onError: (error) => toast.error(error.message),
  })

  return (
    <Sheet open={open} onClose={onClose} title="Места">
      {places.isLoading && <SkeletonCard lines={3} />}

      {places.data && places.data.length === 0 && (
        <EmptyState
          icon="geo-place"
          title="Мест пока нет"
          description="Когда в ленте появится геометка, её можно будет сохранить как место — и дальше она будет подписываться названием."
        />
      )}

      {places.data && places.data.length > 0 && (
        <>
          <Card padded={false} className="px-4">
            {places.data.map((place: Place, index) => (
              <div key={place.id}>
                {index > 0 && <div className="bg-line h-px" />}
                <Row
                  icon={
                    <span
                      aria-hidden
                      className="bg-accent-soft flex size-10 items-center justify-center rounded-[var(--radius-button)]"
                    >
                      <Icon name="geo-place" size={20} />
                    </span>
                  }
                  title={place.title}
                  subtitle={`радиус ${place.radius_m} м`}
                  right={
                    <button
                      type="button"
                      onClick={() => archive.mutate(place.id)}
                      className="text-text-muted tap-target px-2 text-caption"
                    >
                      Удалить
                    </button>
                  }
                />
              </div>
            ))}
          </Card>
          <p className="text-text-muted mt-3 px-1 text-caption leading-snug">
            Если геометка попадает в радиус места, в ленте вместо координат пишется его
            название.
          </p>
        </>
      )}
    </Sheet>
  )
}
